"use client";

import { useState } from "react";
import Image from "next/image";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselCounter,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Skeleton } from "../ui/skeleton";
import { AccommodationImage } from "@/types/accommodations";
import { getAccommodationImageSrc } from "@/utils/getAccommodationImageSrc";

interface MobileImageCarouselProps {
  images: AccommodationImage[];
  onImageClick: (index: number) => void;
}

// Image with skeleton loading state
function ImageWithSkeleton({
  src,
  alt,
  priority = false,
  onClick,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  onClick: () => void;
}) {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="relative w-full h-full cursor-pointer" onClick={onClick}>
      {isLoading && <Skeleton className="absolute inset-0 w-full h-full" />}
      <Image
        src={src}
        alt={alt}
        fill
        className="object-cover"
        priority={priority}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
}

export function MobileImageCarousel({
  images,
  onImageClick,
}: MobileImageCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Update current index when slide changes
  const handleSlideChange = () => {
    if (!api) return;
    setCurrentIndex(api.selectedScrollSnap());
  };

  // Set up event listener when api is ready
  if (api) {
    api.on("select", handleSlideChange);
  }

  const handleImageClick = () => {
    onImageClick(currentIndex);
  };

  return (
    <div className="relative h-[292px]">
      <Carousel
        opts={{
          loop: false,
          align: "start",
        }}
        setApi={setApi}
        dir="rtl"
        className="w-full h-full"
      >
        <CarouselContent className="h-full">
          {images.map((image, index) => (
            <CarouselItem key={index} className="h-[292px] pl-0">
              <ImageWithSkeleton
                src={getAccommodationImageSrc(image?.fileKey)}
                alt={`Villa image ${index + 1}`}
                priority={index === 0}
                onClick={handleImageClick}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselCounter />
      </Carousel>
    </div>
  );
}
